// frontend/src/components/FichaDifunto.jsx
// --- VERSIÓN 22.0: DISEÑO COMPLETO ORIGINAL + MOTOR GPS CORREGIDO ---

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, CircleMarker } from 'react-leaflet';
import { QRCodeCanvas } from 'qrcode.react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-polylinedecorator'; // Importamos la librería para las flechas
import { findSmartPath } from './RouterEngine'; // Importamos el motor GPS
import TarjetaMuro from './TarjetaMuro'; // 
// Configuración de íconos de Leaflet (Para que se vean bien los pines)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});
// 1. BACKEND_URL vacío para que las imágenes busquen en la carpeta local
const BACKEND_URL = ''; 

// 2. Apuntamos al archivo físico JSON de difuntos que ya tienes en la demo
const API_URL_BASE = '/api/v1/difuntos/index.json';

// 3. Las fotos también se buscarán en la raíz de la web
const API_URL_BASE_FOTOS = '';

// 4. La biografía, si la tienes en JSON, apúntala igual, si no, déjala vacía
const API_URL_BIO = '/api/v1/difuntos/index.json';

// ==========================================
// 🛠️ FUNCIONES AUXILIARES (HELPERS)
// ==========================================

// 1. Corrector de Coordenadas (Le da la vuelta si Lat/Lon están invertidos)
function fixCoords(coords) {
    if (!Array.isArray(coords)) return [];
    return coords.map(p => {
        const lat = parseFloat(p[0]);
        const lng = parseFloat(p[1]);
        // En España Lat es ~40 y Lon ~-3. Si Lat < 10 y Lon > 30, están al revés.
        if (Math.abs(lat) < 10 && Math.abs(lng) > 30) return [lng, lat];
        return [lat, lng];
    });
}

// 2. Parseador Seguro (Evita que la web se rompa si el JSON viene mal)
function safeParse(data) {
    if (!data) return [];
    if (Array.isArray(data)) return fixCoords(data); // Si ya es array, solo corregimos orden
    if (typeof data === 'string') {
        try { return fixCoords(JSON.parse(data)); } catch(e) { return []; }
    }
    return [];
}

// 3. Cálculo matemático de la posición del nicho en la calle
function calcularPosicionNicho(numNicho, tramo) {
    try {
        const coords = safeParse(tramo.polilinea_coords);
        if (!coords || coords.length < 2) return null;
        
        const { nicho_inicio_num, total_nichos } = tramo;
        // Evitamos división por cero
        const divisor = total_nichos > 1 ? (total_nichos - 1) : 1;
        const posicionRelativa = (numNicho - nicho_inicio_num) / divisor;
        
        const [latInicio, lonInicio] = coords[0];
        const [latFin, lonFin] = coords[coords.length - 1];
        
        const latCalculada = latInicio + (latFin - latInicio) * posicionRelativa;
        const lonCalculada = lonInicio + (lonFin - lonInicio) * posicionRelativa;
        
        return [latCalculada, lonCalculada];
    } catch (error) { return null; }
}

// ==========================================
// 📍 COMPONENTES DE MAPA (ESTÉTICA)
// ==========================================

// Componente: Zoom Automático a la Ruta
function ZoomToRoute({ routeCoords, show }) {
    const map = useMap();
    useEffect(() => {
        if (show && routeCoords.length > 0) {
            try {
                const bounds = L.latLngBounds(routeCoords);
                if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });
            } catch(e) {}
        }
    }, [show, routeCoords, map]);
    return null;
}

// Componente: Flechas sobre la línea roja
function RouteWithArrows({ routeCoords, show }) {
    const map = useMap();
    const decoratorRef = useRef(null);

    useEffect(() => {
        if (show && routeCoords.length > 0) {
            if (decoratorRef.current) map.removeLayer(decoratorRef.current);
            try {
                decoratorRef.current = L.polylineDecorator(routeCoords, {
                    patterns: [{
                        offset: '25px', 
                        repeat: '80px', // Repetimos las flechas cada 80px
                        symbol: L.Symbol.arrowHead({ 
                            pixelSize: 15, 
                            polygon: false, 
                            pathOptions: { stroke: true, color: 'white', weight: 3 } 
                        })
                    }]
                }).addTo(map);
            } catch(e) {}
        } else {
            if (decoratorRef.current) {
                map.removeLayer(decoratorRef.current);
                decoratorRef.current = null;
            }
        }
        return () => { if (decoratorRef.current) map.removeLayer(decoratorRef.current); };
    }, [show, routeCoords, map]);
    return null;
}

// Componente: Muñeco caminando (Animación)
function AnimatedMarker({ routeCoords, show }) {
    const [position, setPosition] = useState(null);
    const animationFrameId = useRef(null);

    useEffect(() => {
        if (show && routeCoords && routeCoords.length > 1) {
            setPosition(routeCoords[0]);
            let currentSegment = 0;
            let progress = 0;
            const speed = 0.02; // Velocidad del muñeco

            const animate = () => {
                if (currentSegment < routeCoords.length - 1) {
                    const start = routeCoords[currentSegment];
                    const end = routeCoords[currentSegment + 1];
                    
                    if (progress >= 1) {
                        progress = 0;
                        currentSegment++;
                        if (currentSegment >= routeCoords.length - 1) {
                            currentSegment = 0; // Reiniciar bucle
                            setPosition(routeCoords[0]);
                        }
                    } else {
                        progress += speed;
                        const lat = start[0] + (end[0] - start[0]) * progress;
                        const lng = start[1] + (end[1] - start[1]) * progress;
                        setPosition([lat, lng]);
                    }
                    animationFrameId.current = requestAnimationFrame(animate);
                }
            };
            animate();
        }
        return () => { if(animationFrameId.current) cancelAnimationFrame(animationFrameId.current); };
    }, [show, routeCoords]);

    if (!position) return null;

    const walkerIcon = new L.Icon({
        iconUrl: 'https://cdn-icons-png.flaticon.com/512/3753/3753835.png',
        iconSize: [35, 35],
        iconAnchor: [17, 35],
        popupAnchor: [0, -35],
    });

    return show ? <Marker position={position} icon={walkerIcon} zIndexOffset={1000}><Popup>Siguiendo ruta...</Popup></Marker> : null;
}

// ==========================================
// 📍 COMPONENTE PRINCIPAL (FICHA)
// ==========================================
function FichaDifunto() {
    // 1. Capturamos los parámetros limpios del Router
    const { id, slug } = useParams();

    // 2. Usamos 'slug' directamente. Si la URL es /tarazona/difunto/1375, 
    // la variable puebloIdentificado valdrá "tarazona".
    const puebloIdentificado = slug;
    const municipio = slug; 
    const [difunto, setDifunto] = useState(null);
    const [biografia, setBiografia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redDeCalles, setRedDeCalles] = useState([]);
    const [rutaCoords, setRutaCoords] = useState([]);
    const [entradaSeleccionada, setEntradaSeleccionada] = useState(null);
    const [mostrarRuta, setMostrarRuta] = useState(false);
    const currentPageUrl = window.location.href;

    useEffect(() => {
        const fetchData = async () => {
            // Si después de todo sigue siendo undefined o "difunto", paramos antes de llamar al servidor
            if (!puebloIdentificado || puebloIdentificado === 'difunto') {
                console.error("❌ Error: No se pudo identificar el ayuntamiento desde la URL");
                setLoading(false);
                return;
            }

            try {
                // 🚀 LLAMADA AL SERVIDOR
            const urlDifunto = `${API_URL_BASE}/${id}?slug=${puebloIdentificado}`;
            console.log("📡 Pidiendo datos a:", urlDifunto); 
            
            // 1. Cargamos datos del difunto
            const resDifunto = await axios.get(urlDifunto);
            let datosFinales = resDifunto.data;

            // 2. Validación de seguridad inmediata
            if (!datosFinales || !datosFinales.ayuntamiento_id) {
                throw new Error("El registro no tiene ayuntamiento_id.");
            }

            // 3. Mapeo del slug para el botón (f_slug -> funeraria_slug)
            if (datosFinales.f_slug) {
                datosFinales.funeraria_slug = datosFinales.f_slug;
            }

            // 4. Carga de tramos de mapa (Puerto 4000)
            const resMapa = await axios.get(`http://localhost:4000/api/v1/tramos?slug=${puebloIdentificado}`);
            if (Array.isArray(resMapa.data)) setRedDeCalles(resMapa.data);

            // 5. Carga de biografía y fusión de datos
            const resBio = await axios.get(`${API_URL_BIO}/${id}`).catch(() => ({ data: null }));
            
            if (resBio.data) {
                setBiografia(resBio.data);
                // Fusionamos la bio en el objeto principal para no perder nada
                datosFinales = { ...datosFinales, ...resBio.data };
            }

            // 6. ACTUALIZACIÓN ÚNICA DEL ESTADO
            // Esto garantiza que el botón y la bio se pinten a la vez sin undefined
            setDifunto(datosFinales);

        } catch (error) {
            console.error("❌ Fallo crítico:", error.message);
            setDifunto(null); 
        } finally {
            setLoading(false);
        }
    };
    fetchData();
}, [id, puebloIdentificado]);
    // ------------------------------------------------------------------
    // 🔥 CÁLCULOS MEMORIZADOS (EVITAN EL BUCLE INFINITO) 🔥
    // ------------------------------------------------------------------
    const { posicionPin, tramoDelNicho, coordsTramoAzul, fotoDeLapida } = useMemo(() => {
        let pin = null;
        let tramo = null;
        let azul = [];
        let foto = null;

        if (difunto && difunto.tramos_calle) {
            // Buscamos nicho
            const match = difunto.nicho.match(/(\d+)$/);
            const nichoNum = match ? parseInt(match[1], 10) : null;
            // Filtramos solo calles (excluyendo entradas) para buscar el nicho
            const tramosDeCalle = difunto.tramos_calle.filter(t => !t.es_entrada);
            
            if (nichoNum) {
                tramo = tramosDeCalle.find(t => nichoNum >= t.nicho_inicio_num && nichoNum <= t.nicho_fin_num);
                if (tramo) {
                    pin = calcularPosicionNicho(nichoNum, tramo);
                    azul = safeParse(tramo.polilinea_coords); // Usamos el parser seguro para la línea azul
                }
            }
            // Buscamos foto
            if (difunto.fotos && difunto.fotos.length > 0) {
                foto = difunto.fotos.find(f => f.url_foto && f.url_foto.includes('/assets/nicho/')) || difunto.fotos[0];
            }
        }
        return { posicionPin: pin, tramoDelNicho: tramo, coordsTramoAzul: azul, fotoDeLapida: foto };
    }, [difunto]); // Solo se ejecuta si cambia el difunto
    // ------------------------------------------------------------------

    // 2. EFECTO GPS: CALCULAR RUTA AL PULSAR ENTRADA
    useEffect(() => {
        if (entradaSeleccionada && posicionPin && redDeCalles.length > 0) {
            console.log("🔄 Calculando ruta inteligente...");
            
            // Llamamos al motor GPS (RouterEngine)
            const ruta = findSmartPath(entradaSeleccionada, posicionPin, redDeCalles);

            if (ruta) {
                console.log("✅ Ruta encontrada con curvas.");
                setRutaCoords(ruta);
                setMostrarRuta(true);
            } else {
                console.warn("⚠️ No se encontró conexión. Usando línea recta.");
                setRutaCoords([entradaSeleccionada, posicionPin]);
                setMostrarRuta(true);
            }
        }
    }, [entradaSeleccionada, posicionPin, redDeCalles]); // Dependencias seguras

    // --- ESTILOS ORIGINALES COMPLETOS ---
    const styles = {
        container: { display: 'flex', flexWrap: 'wrap', gap: '20px', maxWidth: '1800px', margin: '0 auto' },
        colStandard: { flex: window.innerWidth <= 768 ? '1 1 100%' : '1', minWidth: '350px', padding: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', borderRadius: '8px', backgroundColor: '#fff' },
        colWide: { flex: '3', minWidth: '400px', padding: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', borderRadius: '8px', backgroundColor: '#fff' },
        nichoImage: { width: '100%', height: '600px', objectFit: 'cover', border: '2px solid #ddd', borderRadius: '8px' },
        mapContainer: { height: '600px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd', position: 'relative' },
        sectionTitle: { marginTop: '0', marginBottom: '15px', color: '#333' }
    };

    if (loading) return <p style={{padding:'20px'}}>Cargando ficha completa...</p>;
    if (!difunto) return <p style={{padding:'20px'}}>Difunto no encontrado.</p>;

    // Preparamos las entradas para pintarlas (Puntos Rojos)
    const entradasMapa = redDeCalles.filter(t => t.es_entrada == true || t.es_entrada == 1);

    return (
        <div style={{ padding: '20px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            <Link 
    to={`/${puebloIdentificado}`} 
    style={{ textDecoration: 'none', color: '#007bff', fontWeight: 'bold' }}
>
    ← Volver al buscador de {difunto?.nombre_ayuntamiento || puebloIdentificado}
</Link>
            <hr style={{ margin: '20px 0', borderColor: '#e0e0e0' }} />
            <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '30px' }}>{difunto.nombre} {difunto.apellidos}</h1>
            
            <div style={styles.container}>
                
                {/* --- COLUMNA 1: DATOS PERSONALES --- */}
                <div style={styles.colStandard}>
                    <h3 style={styles.sectionTitle}>Datos Personales</h3>
                    <p><strong>Edad:</strong> {difunto.edad || 'No registrada'}</p>
                    <p><strong>Fecha de defunción:</strong> {difunto.fecha_defuncion ? new Date(difunto.fecha_defuncion).toLocaleDateString('es-ES') : 'No registrada'}</p>
                    
                    <h3 style={styles.sectionTitle}>Datos de Ubicación</h3>
                    <p><strong>Cementerio:</strong> {difunto.nombre_ayuntamiento}</p>
                    <p><strong>Sector:</strong> {difunto.sector}</p>
                    <p><strong>Calle:</strong> {difunto.calle}</p>
                    <p><strong>Nicho:</strong> {difunto.nicho}</p>
                    
                    <div style={{ marginTop: '20px' }}>
                        {posicionPin && (
                            <a href={`http://googleusercontent.com/maps.google.com/?q=${posicionPin[0]},${posicionPin[1]}`} target="_blank" rel="noreferrer" style={{ display: 'block', textAlign: 'center', padding: '10px', backgroundColor: '#4285F4', color: 'white', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
                                🗺️ Cómo llegar (Google GPS)
                            </a>
                        )}
                    </div>
                    
                    <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #eee', borderRadius: '8px', backgroundColor: '#f9f9f9', textAlign: 'center' }}>
                        <QRCodeCanvas value={currentPageUrl} size={128} level={"H"} />
                        <p style={{ margin: '10px 0 0 0', fontSize: '0.9em', color: '#555' }}>Escanear para visitar</p>
                    </div>
                    {/* 🏛️ BLOQUE: ATRIBUCIÓN DE EMPRESA FUNERARIA */}
                    {difunto.funeraria_nombre && (
                        <div style={{ 
                            marginTop: '25px', 
                            padding: '15px', 
                            border: '1px solid #ddd', 
                            borderRadius: '8px', 
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                        }}>
                            <small style={{ color: '#888', textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.7em', letterSpacing: '1px' }}>
                                Servicios prestados por:
                            </small>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                                {difunto.funeraria_logo && (
                                    <img 
                                        src={`${BACKEND_URL}${difunto.funeraria_logo}`} 
                                        alt="Logo Funeraria" 
                                        style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '4px' }} 
                                    />
                                )}
                                <div>
                                    <strong style={{ display: 'block', fontSize: '1em', color: '#333' }}>
                                        {difunto.funeraria_nombre}
                                    </strong>
                                    {difunto.funeraria_estetica?.slogan && (
                                        <span style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                                            "{difunto.funeraria_estetica.slogan}"
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Botón de contacto que ahora lleva a la Landing */}
{difunto.funeraria_estetica?.telefono && (
    <a 
    href={`/${slug}/funeraria/${difunto.funeraria_slug}`}
    style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        marginTop: '15px', 
        padding: '10px', 
        backgroundColor: '#fdf2f2', 
        color: '#b91c1c', 
        textDecoration: 'none', 
        borderRadius: '6px', 
        border: '1px solid #fee2e2',
        fontWeight: 'bold',
        cursor: 'pointer' // Esto asegura que salga la mano al pasar el ratón
    }}
>
    📞 CONTACTO 24H: {difunto.funeraria_estetica?.telefono || 'VER PERFIL'}
</a>
)}
                        </div>
                    )}
                </div>

                {/* --- COLUMNA 2: FOTO DE LA LÁPIDA --- */}
<div style={styles.colStandard}>
    <h3 style={styles.sectionTitle}>Foto de la Lápida</h3>
    {fotoDeLapida ? (
        <a 
            href={fotoDeLapida.url_foto.startsWith('http') ? fotoDeLapida.url_foto : `${API_URL_BASE_FOTOS}${fotoDeLapida.url_foto}`} 
            target="_blank" 
            rel="noopener noreferrer"
        >
            <img 
    src={`http://localhost:4000${fotoDeLapida.url_foto}`} 
    alt="Lápida" 
    style={styles.nichoImage}
    onError={(e) => {
        // Esto te mostrará en la consola la URL exacta que NO encuentra el servidor
        console.error("❌ LA RUTA QUE NO EXISTE ES:", e.target.src); 
    }}
/>
        </a>
    ) : (
        <p>(Foto de lápida no disponible)</p>
    )}
</div>

                {/* --- COLUMNA 3: MAPA INTERACTIVO --- */}
                <div style={styles.colWide}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                        <h3 style={{margin:0, color: '#333'}}>Mapa de Ubicación</h3>
                        {/* Botón para limpiar ruta */}
                        {mostrarRuta && (
                            <button onClick={() => { setMostrarRuta(false); setRutaCoords([]); setEntradaSeleccionada(null); }} style={{ padding: '8px 15px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }} >
                                ❌ Limpiar Ruta
                            </button>
                        )}
                    </div>
                    
                    <MapContainer center={posicionPin || [39.266, -1.918]} zoom={19} style={styles.mapContainer} >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
                        
                        {/* 1. LÍNEA AZUL (Solo la calle del nicho) */}
                        {coordsTramoAzul.length > 0 && (
                            <Polyline positions={coordsTramoAzul} color="#3498db" weight={8} opacity={0.7}>
                                <Popup>Calle: {difunto.calle}</Popup>
                            </Polyline>
                        )}
                        
                        {/* 2. ENTRADAS ROJAS (Puntos interactivos) */}
                        {entradasMapa.map((tramo, i) => {
                            const coords = safeParse(tramo.polilinea_coords);
                            if (coords && coords.length > 0) {
                                return (
                                    <CircleMarker 
                                        key={i} 
                                        center={coords[0]} 
                                        radius={10} 
                                        pathOptions={{ color: 'red', fillColor: '#f00', fillOpacity: 0.9 }}
                                        eventHandlers={{ click: () => setEntradaSeleccionada(coords[0]) }}
                                    >
                                        <Popup>Entrada {tramo.nombre_calle} (Haz clic)</Popup>
                                    </CircleMarker>
                                );
                            }
                            return null;
                        })}

                        {/* 3. RUTA CALCULADA (Roja + Flechas + Animación) */}
                        {rutaCoords.length > 0 && (
                            <>
                                <Polyline positions={rutaCoords} color="#e74c3c" weight={6} opacity={0.8} />
                                <RouteWithArrows routeCoords={rutaCoords} show={mostrarRuta} />
                                <ZoomToRoute routeCoords={rutaCoords} show={mostrarRuta} />
                                <AnimatedMarker routeCoords={rutaCoords} show={mostrarRuta} />
                            </>
                        )}
                        
                        {/* 4. PIN DEL DIFUNTO (Azul) */}
                        {posicionPin && (
                            <Marker position={posicionPin}>
                                <Popup><b>{difunto.nombre}</b><br/>Aquí descansa.</Popup>
                            </Marker>
                        )}
                    </MapContainer>
                </div>
            </div>

            {/* --- SECCIÓN INFERIOR: BIOGRAFÍA --- */}
            {biografia && (
                <div style={{ maxWidth: '1800px', margin: '40px auto 0 auto' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', border: '1px solid #ddd' }}>
                        <div style={{ backgroundColor: '#6c5ce7', color: 'white', padding: '15px 30px' }}>
                            <h2 style={{ margin: 0 }}>📖 Historia de Vida / In Memoriam</h2>
                        </div>
                        <div style={{ padding: '30px', display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                            {biografia.foto_url && (
                                <div style={{ flex: '0 0 350px' }}>
                                    <img src={`http://localhost:4000${biografia.foto_url}`} alt="Recuerdo" style={{ width: '100%', borderRadius: '10px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)' }} />
                                </div>
                            )}
                            <div style={{ flex: '1', minWidth: '300px', fontSize: '1.2em', lineHeight: '1.8', color: '#333', whiteSpace: 'pre-wrap' }}>
                                <div dangerouslySetInnerHTML={{ __html: biografia.texto_html }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* 🕯️ PASO 2: INTEGRACIÓN DEL MURO SOCIAL EN LA FICHA */}
            {biografia && difunto && (
                <div style={{ maxWidth: '800px', margin: '40px auto 100px auto' }}>
                    <h3 style={{ textAlign: 'center', color: '#666', marginBottom: '20px', letterSpacing: '2px' }}>
                        LIBRO DE CONDOLENCIAS Y HOMENAJES
                    </h3>
                    
                    <TarjetaMuro 
                        /* 💉 Construimos el objeto 'post' con los datos de la ficha */
                        post={{
                            difunto_id: id,
                            nombre: difunto.nombre,
                            apellidos: difunto.apellidos,
                            foto_url: biografia.foto_url,
                            texto_html: biografia.texto_html,
                            velas_contador: biografia.velas_contador,
                            fecha_defuncion: difunto.fecha_defuncion,
                            total_mensajes: 0 // Empezará en 0 y se cargará solo
                        }}
                        /* Pasamos la configuración para que la IA sepa el pueblo */
                        config={{
                            id: difunto.ayuntamiento_id,
                            nombre_ayuntamiento: difunto.nombre_ayuntamiento
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default FichaDifunto;